// src/hooks/usePaymentStatus.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../config/firebase';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useUserSession } from '../context/UserSessionContext';

/**
 * Hook for managing payment status and real-time updates
 * Monitors Firestore payments for specific appointments
 */
export function usePaymentStatus(appointmentId) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true); // Start as true to show loading state initially
  const [error, setError] = useState('');
  const { effectiveUserId } = useUserSession();
  const unsubscribeRef = useRef(null);

  // Get current payment status for the appointment
  // Since we now use unified appointmentIds array for all payments,
  // simply check if appointmentId is in the appointmentIds array
  const currentPayment = payments
    .filter(payment => {
      // Check if appointmentId is in the appointmentIds array (works for single and batch payments)
      if (payment.appointmentIds && Array.isArray(payment.appointmentIds)) {
        return payment.appointmentIds.includes(appointmentId);
      }
      return false;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

  const paymentStatus = currentPayment?.status || 'not_requested';

  // Set up real-time listener for user's payments
  useEffect(() => {
    if (!effectiveUserId || !appointmentId) {
      setPayments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Query payments where appointmentIds array contains this appointmentId
      // This works for both single payments (appointmentIds: [id]) and batch payments (appointmentIds: [id1, id2, ...])
      const paymentsRef = collection(db, `userPayments/${effectiveUserId}/payments`);
      const q = query(
        paymentsRef,
        where('appointmentIds', 'array-contains', appointmentId),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const paymentData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              // Convert Firestore timestamps to JS dates
              createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
              updatedAt: data.updatedAt?.toDate?.() || null,
              completedAt: data.completedAt?.toDate?.() || null,
              expiresAt: data.expiresAt?.toDate?.() || new Date(data.expiresAt)
            };
          });

          setPayments(paymentData);
          setLoading(false);
        },
        (err) => {
          console.error('[usePaymentStatus] Error listening to payments:', err);
          setError('שגיאה בטעינת סטטוס תשלום');
          setLoading(false);
        }
      );

      unsubscribeRef.current = unsubscribe;

    } catch (err) {
      console.error('[usePaymentStatus] Error setting up listener:', err);
      setError('שגיאה בהגדרת מעקב תשלום');
      setLoading(false);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [effectiveUserId, appointmentId]);

  // Clear error function
  const clearError = useCallback(() => {
    setError('');
  }, []);

  // Check if payment can be requested
  const canRequestPayment = useCallback(() => {
    const allowedStatuses = ['not_requested', 'failed', 'expired', 'cancelled'];
    return allowedStatuses.includes(paymentStatus);
  }, [paymentStatus]);

  // Check if payment can be cancelled
  const canCancelPayment = useCallback(() => {
    return paymentStatus === 'pending' && currentPayment;
  }, [paymentStatus, currentPayment]);

  // Check if payment is completed
  const isPaymentCompleted = useCallback(() => {
    return paymentStatus === 'completed';
  }, [paymentStatus]);

  // Check if payment is pending
  const isPaymentPending = useCallback(() => {
    return paymentStatus === 'pending';
  }, [paymentStatus]);

  // Get payment status info for UI display
  const getPaymentStatusInfo = useCallback(() => {
    const statusLabels = {
      'not_requested': 'טרם נדרש תשלום',
      'pending': 'ממתין לתשלום',
      'completed': 'שולם',
      'failed': 'תשלום נכשל',
      'expired': 'קישור תשלום פג',
      'cancelled': 'תשלום בוטל'
    };

    const statusColors = {
      'not_requested': 'gray',
      'pending': 'yellow',
      'completed': 'green',
      'failed': 'red',
      'expired': 'orange',
      'cancelled': 'gray'
    };

    return {
      status: paymentStatus,
      label: statusLabels[paymentStatus] || 'לא ידוע',
      color: statusColors[paymentStatus] || 'gray',
      canRequest: canRequestPayment(),
      canCancel: canCancelPayment(),
      isCompleted: isPaymentCompleted(),
      isPending: isPaymentPending(),
      currentPayment
    };
  }, [paymentStatus, canRequestPayment, canCancelPayment, isPaymentCompleted, isPaymentPending, currentPayment]);

  // Get time until payment expiration
  const getTimeUntilExpiration = useCallback(() => {
    if (!currentPayment?.expiresAt || paymentStatus !== 'pending') {
      return null;
    }

    const now = new Date();
    const expiration = new Date(currentPayment.expiresAt);
    const diffMs = expiration.getTime() - now.getTime();

    if (diffMs <= 0) {
      return {
        isExpired: true,
        minutes: 0,
        seconds: 0,
        displayText: 'פג תוקף'
      };
    }

    const minutes = Math.floor(diffMs / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    return {
      isExpired: false,
      minutes,
      seconds,
      displayText: `${minutes}:${seconds.toString().padStart(2, '0')}`
    };
  }, [currentPayment?.expiresAt, paymentStatus]);

  // Get all payment attempts for this appointment
  const getPaymentHistory = useCallback(() => {
    return payments.filter(payment =>
      payment.appointmentIds && payment.appointmentIds.includes(appointmentId)
    );
  }, [payments, appointmentId]);

  return {
    // State - all payments are already filtered by the query, but double-check here
    payments: payments.filter(payment =>
      payment.appointmentIds && payment.appointmentIds.includes(appointmentId)
    ),
    currentPayment,
    paymentStatus,
    loading,
    error,

    // Status checks
    canRequestPayment,
    canCancelPayment,
    isPaymentCompleted,
    isPaymentPending,

    // Utilities
    getPaymentStatusInfo,
    getTimeUntilExpiration,
    getPaymentHistory,
    clearError
  };
}

/**
 * Hook for monitoring unpaid payments for dashboard
 * Returns simple list of unpaid payments with customer info and time elapsed
 */
export function useUnpaidPayments() {
  const [unpaidPayments, setUnpaidPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { effectiveUserId } = useUserSession();
  const unsubscribeRef = useRef(null);

  // Set up real-time listener for unpaid payments only
  useEffect(() => {
    if (!effectiveUserId) {
      setUnpaidPayments([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const paymentsRef = collection(db, `userPayments/${effectiveUserId}/payments`);
      const q = query(
        paymentsRef,
        where('status', 'in', ['pending', 'failed', 'expired', 'completed']),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        q,
        async (snapshot) => {
          const paymentData = snapshot.docs.map(doc => {
            const data = doc.data();
            const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
            const now = new Date();
            const timeSinceRequest = now - createdAt;

            return {
              id: doc.id,
              appointmentId: data.appointmentId, // Legacy field for backward compatibility
              appointmentIds: data.appointmentIds || (data.appointmentId ? [data.appointmentId] : []), // New array field
              customerName: data.customerName,
              customerPhone: data.customerPhone || '',
              customerEmail: data.customerEmail || '',
              serviceName: data.serviceName,
              amount: data.amount,
              status: data.status,
              createdAt: createdAt,
              expiresAt: data.expiresAt?.toDate?.() || new Date(data.expiresAt),
              invoice: data.invoice || false,

              // Additional fields needed for PaymentAPI.generatePaymentContext
              appointmentDate: data.appointmentDate || '',
              appointmentTime: data.appointmentTime || '',

              // Calculated fields
              timeSinceRequest: timeSinceRequest,
              hoursElapsed: Math.floor(timeSinceRequest / (1000 * 60 * 60)),
              minutesElapsed: Math.floor((timeSinceRequest % (1000 * 60 * 60)) / (1000 * 60)),
              timeDisplay: formatTimeElapsed(timeSinceRequest)
            };
          });

          // Check and update expired payments BEFORE setting state
          await checkAndUpdateExpiredPayments(paymentData);

          console.log('Unpaid payments updated:', paymentData.length);
          setUnpaidPayments(paymentData);
          setLoading(false);
        },
        (err) => {
          console.error('Error listening to unpaid payments:', err);
          setError('שגיאה בטעינת תשלומים לא שולמו');
          setLoading(false);
        }
      );

      unsubscribeRef.current = unsubscribe;

      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };

    } catch (err) {
      console.error('Error setting up unpaid payments listener:', err);
      setError('שגיאה בהגדרת מעקב תשלומים');
      setLoading(false);
    }
  }, [effectiveUserId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  // Format time elapsed since payment request
  const formatTimeElapsed = (timeDiff) => {
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} ${days === 1 ? 'יום' : 'ימים'}`;
    } else if (hours > 0) {
      return `${hours} ${hours === 1 ? 'שעה' : 'שעות'}`;
    } else {
      return `${minutes} ${minutes === 1 ? 'דקה' : 'דקות'}`;
    }
  };

  const checkAndUpdateExpiredPayments = async (paymentData) => {
    const now = new Date();
    const expiredPayments = paymentData.filter(payment =>
      payment.status === 'pending' &&
      payment.expiresAt &&
      new Date(payment.expiresAt) < now
    );

    // Update expired payments directly in Firestore
    for (const payment of expiredPayments) {
      try {
        const paymentRef = doc(db, `userPayments/${effectiveUserId}/payments`, payment.id);
        await updateDoc(paymentRef, {
          status: 'expired',
          updatedAt: new Date()
        });
        console.log('Payment marked as expired:', payment.id);
      } catch (error) {
        console.error('Error marking payment as expired:', error);
      }
    }
  };

  // Get count by status
  const getCountByStatus = useCallback(() => {
    return {
      pending: unpaidPayments.filter(p => p.status === 'pending').length,
      failed: unpaidPayments.filter(p => p.status === 'failed').length,
      expired: unpaidPayments.filter(p => p.status === 'expired').length,
      completed: unpaidPayments.filter(p => p.status === 'completed').length, // Add completed
      total: unpaidPayments.length
    };
  }, [unpaidPayments]);

  const deletePayment = async (paymentId) => {
    try {
      const paymentRef = doc(db, `userPayments/${effectiveUserId}/payments`, paymentId);
      await deleteDoc(paymentRef);
      console.log('Payment deleted:', paymentId);
    } catch (error) {
      console.error('Error deleting payment:', error);
      throw error;
    }
  };

  return {
    unpaidPayments,
    loading,
    error,
    getCountByStatus,
    deletePayment
  };
}